import ExcelJS from 'exceljs';

export interface ExcelExportOptions {
  filename?: string;
  sheetName?: string;
  headerStyle?: Partial<ExcelJS.Style>;
  includeGroupSummary?: boolean;
}

export interface ExcelColumn {
  header: string;
  key: string;
  width?: number;
}

export interface GroupedExportData {
  isGroup: boolean;
  level: number;
  groupValue?: string;
  data: Record<string, unknown>;
  aggregations?: Record<string, number>;
}

const defaultHeaderStyle: Partial<ExcelJS.Style> = {
  font: { bold: true, color: { argb: 'FFFFFFFF' } },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF4F46E5' }
  },
  alignment: { horizontal: 'center', vertical: 'middle' },
  border: {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  }
};

const groupRowStyle: Partial<ExcelJS.Style> = {
  font: { bold: true },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF3F4F6' }
  }
};

export async function exportToExcel<T extends Record<string, unknown>>(
  data: T[],
  columns: ExcelColumn[],
  options: ExcelExportOptions = {}
): Promise<void> {
  const {
    filename = 'export.xlsx',
    sheetName = 'Data',
    headerStyle = defaultHeaderStyle
  } = options;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Gorkem Components';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(sheetName);

  worksheet.columns = columns.map(col => ({
    header: col.header,
    key: col.key,
    width: col.width || 15
  }));

  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    Object.assign(cell.style, headerStyle);
  });
  headerRow.height = 25;

  data.forEach((row) => {
    const excelRow = worksheet.addRow(row);
    excelRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
      };
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

export async function exportGroupedToExcel(
  data: GroupedExportData[],
  columns: ExcelColumn[],
  options: ExcelExportOptions = {}
): Promise<void> {
  const {
    filename = 'export.xlsx',
    sheetName = 'Data',
    headerStyle = defaultHeaderStyle
  } = options;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Gorkem Components';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(sheetName);

  worksheet.columns = columns.map(col => ({
    header: col.header,
    key: col.key,
    width: col.width || 15
  }));

  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    Object.assign(cell.style, headerStyle);
  });
  headerRow.height = 25;

  data.forEach((item) => {
    if (item.isGroup) {
      const groupRow = worksheet.addRow({
        [columns[0].key]: '  '.repeat(item.level) + '▼ ' + item.groupValue
      });
      groupRow.eachCell((cell) => {
        Object.assign(cell.style, groupRowStyle);
      });

      if (item.aggregations) {
        Object.entries(item.aggregations).forEach(([key, value]) => {
          const colIndex = columns.findIndex(c => c.key === key);
          if (colIndex >= 0) {
            const cell = groupRow.getCell(colIndex + 1);
            cell.value = value;
          }
        });
      }
    } else {
      const excelRow = worksheet.addRow(item.data);
      excelRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
        };
      });
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

function downloadBuffer(buffer: ExcelJS.Buffer, filename: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
